import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Button, Chip, Divider, Typography } from "@mui/material";

import { formatLocalizedTimeAgo } from "../../../../../../utils/formatDate";
import { getServiceRequestPageRoute } from "../../../../../../routers/routeUtils";

import { ServiceContract } from "../../../../types/service.type";
import { useRequestUserData } from "../../../../hooks/useRequestUserData";
import { getUpcomingMilestone } from "../../../../utils/getUpcomingMilestone";
import { useServiceStatusFlags } from "../../../../hooks/useServiceStatusFlags";
import { useServiceRequestActions } from "../../../../hooks/useServiceRequestActions";
import { CancelModal, useCancelModal } from "../../../../components/CancelModal";
import { ServiceContractStatusEnum } from "../../../../enum/service.enum";

const ServiceContractCard = ({ contract }: { contract: ServiceContract }) => {
  const { t, i18n } = useTranslation("goService", { keyPrefix: "myRequestsPage" });
  const { openCancelModal, handleOpenCancelModal, handleCloseCancelModal } = useCancelModal();

  const { serviceRequest, milestones, id: contractId } = contract;
  const { media, firstTwoChr, fullName, role, profileLink } = useRequestUserData(serviceRequest);
  const { name } = serviceRequest.service;

  const { timeUntilDue, isFuture } = getUpcomingMilestone(milestones, i18n.language);

  const { isProvider, isInitiated, isNegotiated, isContracted } = useServiceStatusFlags(serviceRequest);

  const {
    handleAcceptContract,
    handleEndContract,
    handleWithdrawRequest,
    handleCreateContract,
    handleNegotiateContract,
    isResponding,
    isWithdrawing,
  } = useServiceRequestActions(serviceRequest, contractId);

  const handleEndContractFromModal = () => {
    handleEndContract();
    handleCloseCancelModal();
  };

  const handleWithdrawRequestFromModal = () => {
    handleWithdrawRequest();
    handleCloseCancelModal();
  };

  const isContractDenied = contract.status === ServiceContractStatusEnum.Denied;

  const getActionButtons = () => {
    // Provider logic
    if (isProvider) {
      if (isContracted && !isContractDenied) {
        return (
          <>
            <Button variant="outlined-sm" onClick={handleOpenCancelModal} disabled={isResponding}>
              {t("endContract")}
            </Button>
            <CancelModal
              open={openCancelModal}
              closeModal={handleCloseCancelModal}
              handelCancel={handleEndContractFromModal}
              title={t("areYouSureToRejectThisContract")}
              subTitle={t("onceRejectedThisContractThisActionCannotBeUndone")}
            />
          </>
        );
      }

      if (isNegotiated) {
        return (
          <Box sx={styles.actionsContainer}>
            <Button variant="primary-sm" onClick={handleAcceptContract} disabled={isResponding}>
              {t("acceptContract")}
            </Button>
            <Button variant="outlined-sm" onClick={handleOpenCancelModal} disabled={isResponding}>
              {t("endContract")}
            </Button>
            <CancelModal
              open={openCancelModal}
              closeModal={handleCloseCancelModal}
              handelCancel={handleEndContractFromModal}
              title={t("areYouSureToRejectThisContract")}
              subTitle={t("onceRejectedThisContractThisActionCannotBeUndone")}
            />
          </Box>
        );
      }

      return null;
    }

    // Requester logic
    if (isNegotiated) {
      return (
        <Button variant="primary-sm" onClick={handleNegotiateContract}>
          {t("negotiateContract", "Negotiate Contract")}
        </Button>
      );
    }

    if (isInitiated) {
      return (
        <Box sx={styles.actionsContainer}>
          <Button variant="primary-sm" onClick={handleCreateContract}>
            {t("createContract")}
          </Button>
          <Button variant="outlined-sm" onClick={handleOpenCancelModal} disabled={isResponding}>
            {t("withdrawRequest")}
          </Button>
          <CancelModal
            open={openCancelModal}
            closeModal={handleCloseCancelModal}
            handelCancel={handleWithdrawRequestFromModal}
            title={t("areYouSureToRejectThisContract")}
            subTitle={t("onceRejectedThisContractThisActionCannotBeUndone")}
          />
        </Box>
      );
    }

    return null;
  };

  const actions = getActionButtons();

  return (
    <Box sx={styles.container}>
      <Box sx={styles.serviceNameContainer}>
        <Typography sx={styles.serviceName} component={Link} to={getServiceRequestPageRoute(serviceRequest.id)}>
          {name}
        </Typography>
        <Chip
          label={t("milestone", { date: timeUntilDue })}
          sx={isFuture ? styles.futureMilestoneChip : styles.pastMilestoneChip}
        />
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.serviceProviderContainer}>
        <Avatar sx={styles.serviceProviderAvatar} src={media}>
          <Typography>{firstTwoChr}</Typography>
        </Avatar>
        <Box>
          <Typography component={Link} to={profileLink} sx={styles.serviceProviderName}>
            {fullName}
          </Typography>
          <Typography sx={styles.serviceProviderRole}>{role}</Typography>
        </Box>
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.serviceDetailsContainer}>
        <Typography sx={styles.serviceDetailsTime}>
          {formatLocalizedTimeAgo(serviceRequest.createdAt, i18n.language)}
        </Typography>

        {actions}
      </Box>
    </Box>
  );
};

export default ServiceContractCard;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #E7E7E7",
  },
  serviceNameContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  serviceName: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Inter",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    textDecoration: "none",
  },
  futureMilestoneChip: {
    padding: "2px 8px",
    borderRadius: "16px",
    fontFamily: "Inter",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    height: "fit-content",
    color: "var(--Brand-600, #008B07)",
    border: "1px solid var(--Brand-500, #00B809)",
    background: "var(--Brand-25, #F6FFF5)",
    "& .MuiChip-label": {
      padding: 0,
    },
  },
  pastMilestoneChip: {
    padding: "2px 8px",
    borderRadius: "16px",
    fontFamily: "Inter",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    height: "fit-content",
    color: "var(--Gray-600, #475467)",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    background: "var(--Gray-50, #F9FAFB)",
    "& .MuiChip-label": {
      padding: 0,
    },
  },
  divider: {
    mx: "-16px",
  },
  serviceProviderContainer: {
    display: "flex",
    gap: "12px",
  },
  serviceProviderAvatar: {
    width: "40px",
    height: "40px",
  },
  serviceProviderName: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    textDecoration: "none",
  },
  serviceProviderRole: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  serviceDetailsContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  serviceDetailsTime: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  serviceContainer: {
    display: "flex",
    gap: "8px",
  },
  actionsContainer: {
    display: "flex",
    gap: "8px",
  },
};
